"use client";

import { Navbar } from "@/components/navbar";
import { HeroSection } from "@/components/hero-section";
import { HowItWorksSection } from "@/components/how-it-works-section";
import { ProblemSection } from "@/components/problem-section";
import { FeaturesSection } from "@/components/features-section";
import { PricingSection } from "@/components/pricing-section";
import { FaqSection } from "@/components/faq-section";
import { CtaSection } from "@/components/cta-section";
import { useRouter } from "next/navigation";
import { WAITLIST } from "@/lib/constants";

export default function Home() {
  const router = useRouter();

  const handleNavigation = (path: string) => {
    if (WAITLIST) {
      router.push("/waitlist");
    } else {
      router.push(path);
    }
  };

  return (
    <main className="min-h-screen bg-black text-white overflow-x-hidden relative">
      <Navbar />
      {/* Global gradient overlay for smooth transitions */}
      <div className="fixed w-screen h-screen inset-0 bg-gradient-to-b from-purple-950/10 via-black/50 to-black pointer-events-none opacity-50"></div>

      {/* Hero Section - Smooth gradient transition */}
      <section className="w-screen relative">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-950/30 via-purple-900/10 to-transparent pointer-events-none"></div>
        <div className="max-w-7xl mx-auto relative">
          <HeroSection />
        </div>
      </section>

      {/* Problem Section - Subtle fade transition */}
      <section className="w-screen relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black to-purple-950/10 pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 md:py-16 relative">
          <ProblemSection />
        </div>
      </section>

      {/* How It Works Section - Gradient blend */}
      <section className="w-screen relative">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-950/10 via-black to-purple-900/5 pointer-events-none"></div>
        <HowItWorksSection />
      </section>

      {/* Features Section - Smooth accent */}
      <section className="w-screen relative">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/5 via-black to-purple-950/10 pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 md:py-16 relative">
          <FeaturesSection />
        </div>
      </section>

      {/* Pricing Section - Subtle transition */}
      <section className="w-screen relative">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-950/10 via-black to-purple-900/5 pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 md:py-16 relative">
          <div className="flex justify-center">
            <div className="w-full max-w-5xl">
              <PricingSection />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section - Gradient flow */}
      <section className="w-screen relative">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/5 via-black to-purple-950/10 pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 md:py-16 relative">
          <FaqSection />
        </div>
      </section>

      {/* CTA Section - Final gradient */}
      <section className="w-screen relative">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-950/10 via-purple-900/5 to-black pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 md:py-16 relative">
          <CtaSection />
        </div>
      </section>

      {/* Footer */}
      <footer className="w-screen relative bg-black/90">
        <div className="absolute inset-0 bg-gradient-to-t from-transparent via-purple-950/5 to-black/20 pointer-events-none opacity-30"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative">
          <div className="border-t border-gray-800 pt-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {/* Company Info */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white">LeakAlert</h3>
                <p className="text-sm text-gray-400">Get a text alert as soon as a new leak is detected.</p>
              </div>
              
              {/* Quick Links */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white">Quick Links</h3>
                <ul className="space-y-2">
                  <li>
                    <button 
                      onClick={() => handleNavigation("#features")} 
                      className="text-sm text-gray-400 hover:text-white transition-colors"
                    >
                      Features
                    </button>
                  </li>
                  <li>
                    <button 
                      onClick={() => handleNavigation("#pricing")} 
                      className="text-sm text-gray-400 hover:text-white transition-colors"
                    >
                      Pricing
                    </button>
                  </li>
                  <li>
                    <button 
                      onClick={() => handleNavigation("#faq")} 
                      className="text-sm text-gray-400 hover:text-white transition-colors"
                    >
                      FAQ
                    </button>
                  </li>
                </ul>
              </div>
              
              {/* Legal */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white">Legal</h3>
                <ul className="space-y-2">
                  <li>
                    <button 
                      onClick={() => handleNavigation("/privacy")} 
                      className="text-sm text-gray-400 hover:text-white transition-colors"
                    >
                      Privacy Policy
                    </button>
                  </li>
                  <li>
                    <button 
                      onClick={() => handleNavigation("/terms")} 
                      className="text-sm text-gray-400 hover:text-white transition-colors"
                    >
                      Terms of Service
                    </button>
                  </li>
                </ul>
              </div>
              
              {/* Social */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white">Connect</h3>
                <ul className="space-y-2">
                  <li>
                    <button 
                      onClick={() => handleNavigation("https://discord.gg/leakalert")} 
                      className="text-sm text-gray-400 hover:text-white transition-colors"
                    >
                      Discord
                    </button>
                  </li>
                  <li>
                    <button 
                      onClick={() => handleNavigation("https://twitter.com/leakalert")} 
                      className="text-sm text-gray-400 hover:text-white transition-colors"
                    >
                      Twitter
                    </button>
                  </li>
                </ul>
              </div>
            </div>
            
            {/* Copyright */}
            <div className="mt-8 pt-8 border-t border-gray-800">
              <p className="text-center text-sm text-gray-400">
                © {new Date().getFullYear()} LeakAlert. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </main>
  );
}